/**
 * utils/sessionHandoff.js — приём сессии, приехавшей с другого origin.
 *
 * Зачем это вообще нужно.
 *
 * pm_token лежит в localStorage, а localStorage привязан к origin. В конце
 * онбординга фронт уводит человека с app.fixty.io на <его-тенант>.fixty.io —
 * другой origin, пустой слот, экран логина. Со стороны выглядит как «выкинуло
 * сразу после регистрации», хотя аккаунт цел и пароль верный.
 *
 * Лечится передачей сессии через URL. Боевой 30-дневный JWT туда класть нельзя
 * (остаётся в истории браузера и в списке недавних вкладок), поэтому исходный
 * origin берёт у бэка одноразовый минутный код (POST /auth/handoff) и кладёт
 * его во ФРАГМЕНТ адреса: `https://sub.fixty.io/#h=<код>`.
 *
 * Фрагмент выбран не случайно — он не уходит на сервер ни в запросе, ни в
 * Referer, то есть код не оседает в логах nginx и в аналитике.
 *
 * Здесь мы этот код вылавливаем, меняем на полноценный токен и вычищаем
 * адресную строку до того, как отрисуется приложение.
 */

import { track } from '../analytics';

const BASE = process.env.REACT_APP_API_URL || '/api';
const PARAM = 'h';

/** Убрать код из адресной строки, не добавляя записи в историю. */
function stripHashParam(params) {
  params.delete(PARAM);
  const rest = params.toString();
  const clean = window.location.pathname + window.location.search + (rest ? `#${rest}` : '');
  try {
    window.history.replaceState(null, '', clean);
  } catch {
    // Приватный режим Safari умеет ругаться на replaceState — не повод падать,
    // код всё равно уже одноразовый и через минуту протухнет сам.
  }
}

/**
 * Забрать сессию из фрагмента, если она там есть.
 *
 * Вызывается ДО рендера приложения: AuthProvider на маунте читает localStorage
 * один раз, и если токен появится позже, пользователь успеет увидеть логин.
 *
 * Никогда не реджектится — неудачный обмен означает лишь, что человек увидит
 * обычный экран входа, то есть ровно то поведение, что было до этого файла.
 *
 * @returns {Promise<boolean>} true, если сессия принята
 */
export async function consumeSessionHandoff() {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!raw) return false;

  const params = new URLSearchParams(raw);
  const code = params.get(PARAM);
  if (!code) return false;

  // Чистим адрес сразу, до сетевого запроса: если вкладку перезагрузят во время
  // обмена, второй раз код уже не сработает (бэк держит его одноразовым) и
  // пользователь застрянет на ошибке вместо логина.
  stripHashParam(params);

  try {
    const res = await fetch(`${BASE}/auth/handoff/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handoff: code }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (!data?.token) return false;

    // Затираем токен, который мог лежать на этом origin. Это осознанно: сюда
    // приезжают только со своего же субдомена и только сразу после онбординга,
    // то есть «прежний» токен принадлежит тому же человеку.
    localStorage.setItem('pm_token', data.token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Увести залогиненного пользователя на канонический хост его тенанта
 * (поддомен или custom_domain), забрав с собой сессию и текущий путь.
 *
 * Триггер — App.js: CanonicalHostGate. Симметрично consumeSessionHandoff():
 * тот принимает код на целевом origin, этот — производит его на исходном.
 * Голый `location.href` сюда ставить нельзя по той же причине, что и в
 * OnboardingSetupPage.js — pm_token в localStorage привязан к origin, и
 * человек с валидной сессией приехал бы на чужой домен к экрану логина.
 *
 * Не смогли получить код — уезжаем без него: человек увидит логин уже на
 * верном домене, а не приложение на чужом бренде. Это не поломка, а прежнее
 * поведение (до появления этого редиректа).
 *
 * @param {string} targetHost — хост назначения без протокола, напр. acme.fixty.io
 */
export async function redirectToCanonicalHost(targetHost) {
  const path = window.location.pathname + window.location.search;
  let target = `${window.location.protocol}//${targetHost}${path}`;
  let ok = false;
  try {
    const res = await fetch(`${BASE}/auth/handoff`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('pm_token')}`,
      },
    });
    if (res.ok) {
      const { handoff } = await res.json();
      if (handoff) {
        target += `#h=${encodeURIComponent(handoff)}`;
        ok = true;
      }
    }
  } catch {
    // см. коммент выше
  }
  // Сессия ПЕРЕЕЗЖАЕТ, а не копируется: токен на исходном origin больше не
  // нужен, и оставлять его — значит держать живую копию, которая при следующем
  // заходе на этот домен снова увезёт человека в аккаунт (в т.ч. после выхода
  // на целевом домене, если токен выпущен до появления sid). Стираем только при
  // успешном коде: без кода человек и так приедет к логину.
  if (ok) {
    try { localStorage.removeItem('pm_token'); } catch { /* приватный режим */ }
  }
  track('canonical_host_redirect', { ok, target_host: targetHost });
  window.location.href = target;
}
